import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import editorange from "../assets/images/icon/edit-orange.png";
import Input from "components/Input";
import Select from "components/Select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updatePaySchedule } from "services/paySchedules";

const FREQUENCY_OPTIONS = [
    { value: 'Weekly', label: 'Weekly' },
    { value: 'BiWeekly', label: 'Bi-Weekly' },
    { value: 'SemiMonthly', label: 'Semi-Monthly' },
    { value: 'Monthly', label: 'Monthly' },
];

const toDateInput = (val) => {
    if (!val) return '';
    try { return new Date(val).toISOString().split('T')[0]; } catch { return ''; }
};
const toISO = (val) => val ? new Date(val).toISOString() : undefined;

const buildForm = (s) => ({
    name: s?.name || '',
    frequency: s?.frequency || '',
    firstPeriodEnd: toDateInput(s?.anchorEndOfPayPeriod),
    firstPayday: toDateInput(s?.anchorPayDate),
});

function PayScheduleDetailsModal({ schedule, open, onClose, defaultEditing = false }) {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    const [form, setForm] = useState(buildForm(schedule));
    const [current, setCurrent] = useState(schedule);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (schedule) {
            setCurrent(schedule);
            setForm(buildForm(schedule));
            setIsEditing(defaultEditing);
        }
    }, [schedule, defaultEditing]);

    const mutation = useMutation({
        mutationFn: updatePaySchedule,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pay-schedules'] });
            setCurrent(prev => ({
                ...prev,
                name: form.name,
                frequency: form.frequency,
                anchorPayDate: form.firstPayday ? new Date(form.firstPayday).toISOString() : prev.anchorPayDate,
                anchorEndOfPayPeriod: form.firstPeriodEnd ? new Date(form.firstPeriodEnd).toISOString() : prev.anchorEndOfPayPeriod,
            }));
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({
            scheduleId: current.id,
            name: form.name,
            frequency: form.frequency,
            firstPayday: toISO(form.firstPayday),
            firstPeriodEnd: toISO(form.firstPeriodEnd),
        });
    };

    const handleClose = () => { mutation.reset(); onClose(); };

    if (!current) return null;

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Pay Schedule Detail</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body>
                {!isEditing ? (
                    <>
                        <div className='d-flex justify-content-between align-items-center'>
                            <div>
                                <p className='gray-title my-2'>Schedule Id</p>
                                <h6 className='id-title fw-medium text-black m-0'>{current.id}</h6>
                            </div>
                            <img className='cursor-pointer' src={editorange} alt="edit" onClick={() => setIsEditing(true)} />
                        </div>
                        <div className='border-bottom my-3 pb-4'>
                            <h2 className='sub-title'>General Information</h2>
                            <div className='d-flex workplace-detail-section mt-4'>
                                <div>
                                    <p className='gray-title my-2'>Name</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.name || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Frequency</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.frequency || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>First Payday</p>
                                    <h6 className='id-title fw-medium text-black m-0'>
                                        {current.anchorPayDate ? new Date(current.anchorPayDate).toLocaleDateString() : '—'}
                                    </h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>First Period End</p>
                                    <h6 className='id-title fw-medium text-black m-0'>
                                        {current.anchorEndOfPayPeriod ? new Date(current.anchorEndOfPayPeriod).toLocaleDateString() : '—'}
                                    </h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Active Status</p>
                                    <button className='active-button py-1 px-4'>{current.active ? 'Active' : 'Inactive'}</button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSave}>
                        <div className='row mt-3'>
                            <div className='col-lg-6'>
                                <Input label="Name" fullWidth variant='flat' name='name' value={form.name} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6'>
                                <Select label="Frequency" fullWidth placeholder='Select frequency' options={FREQUENCY_OPTIONS} name='frequency' value={form.frequency} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 mt-3'>
                                <Input label="First Period End" type='date' fullWidth variant='flat' name='firstPeriodEnd' value={form.firstPeriodEnd} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 mt-3'>
                                <Input label="First Pay Day" type='date' fullWidth variant='flat' name='firstPayday' value={form.firstPayday} onChange={handleChange} />
                            </div>
                        </div>
                        {mutation.isError && <p className="text-danger mt-3">Failed to update pay schedule. Please try again.</p>}
                        <div className='d-flex gap-2 mt-4'>
                            <button type='submit' className='button-orange py-2 px-4 rounded-pill' disabled={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type='button' className='btn btn-outline-secondary rounded-pill py-2 px-4' onClick={() => setIsEditing(false)} disabled={mutation.isPending}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default PayScheduleDetailsModal;
